import { useState } from 'react'

import { IconButton, Menu, useMediaQuery } from '@mui/material'
import { usePermissions } from 'commons/customHooks/useAuth'
import type { statusBgKey } from 'commons/styles/StatusBG'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { toFirsLetterUpper } from 'commons/utils/string'
import { Icon, Modal, StatusType } from 'components'
import type { OriginOrder } from 'constants/enums'
import { StatusOV } from 'container/Home/components/Banners/interface'
import { getEDIRowIcon } from 'container/ManageOrders/utils'
import SapModal, { type SapModalTypes } from 'container/ManageOVDetails/components/SapModal'
import ModalDuplicate from 'container/SalesOrder/components/ModalDuplicate'
import ModalPendingEDI from 'container/SalesOrder/components/ModalPendingEDI'
import ModalPostOn, { type ClientsListInterface } from 'container/SalesOrder/components/ModalPostOn'
import ModalTracking from 'container/SalesOrder/components/ModalTracking'
import CancelOvForm, {
  type CancelOvReason,
} from 'container/SalesOrder/components/OvsTab/components/CancelOvForm'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import type { SapOptionsToOvStatus } from 'store/OVContext/ovContextTypes'
import { getReasonsRefusal } from 'store/OVContext/ovSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import type { ActionPerm } from '../Header/constants'
import * as S from './styles'

export type ToolBarProps = {
  orderNumber?: string
  ovPortalNumber?: string
  ovSapNumber?: string
  materialTypeMTO?: boolean
  orderStatus?: 'initial' | 'created' | 'manage' | 'ordered'
  isDisableButtonCancel?: boolean
  isButtonCancel?: boolean
  onClickCancel?: (data: CancelOvReason) => void
  isDisableButtonRescueData?: boolean
  isButtonRescueData?: boolean
  onClickRescueData?: () => void
  isDisableButtonDownload?: boolean
  isButtonDownload?: boolean
  onClickDownload?: () => void
  isDisableButtonTitles?: boolean
  isButtonTitles?: boolean
  onClickTitles?: () => void
  isDisableButtonDuplicate?: boolean
  isButtonDuplicate?: boolean
  onClickDuplicate?: () => void
  isDisableButtonPostOn?: boolean
  isButtonPostOn?: boolean
  onClickPostOn?: (array: ClientsListInterface[]) => Promise<void>
  isDisableButtonShare?: boolean
  isButtonShare?: boolean
  onClickShare?: () => void
  isDisableButtonTrack?: boolean
  isButtonTrack?: boolean
  isButtonPendingEDI?: boolean
  isDisableButtonPendingEDI?: boolean
  isDisableButtonSave?: boolean
  isLoadingButtonSave?: boolean
  isButtonSave?: boolean
  onClickSave?: () => void
  isDisableButtonDelete?: boolean
  isButtonDelete?: boolean
  onClickDelete?: () => void
  fullWidth?: boolean
  showDeleteModal?: boolean
  showButtonMore?: boolean
  onClickSap?: () => void
  sapButtonsOptions?: SapOptionsToOvStatus
  disableToolbar?: boolean
  isND?: boolean
  originOrder?: OriginOrder
}

export const ToolBar = ({
  orderNumber,
  materialTypeMTO = false,
  orderStatus = 'initial',
  isDisableButtonCancel = false,
  isButtonCancel = false,
  onClickCancel = () => null,
  isDisableButtonRescueData = false,
  isButtonRescueData = false,
  onClickRescueData = () => null,
  isDisableButtonDownload = false,
  isButtonDownload = false,
  onClickDownload = () => null,
  isDisableButtonDuplicate = false,
  isButtonDuplicate = false,
  onClickDuplicate = () => null,
  isDisableButtonPostOn = false,
  isButtonPostOn = false,
  onClickPostOn,
  isDisableButtonShare = false,
  isButtonShare = false,
  onClickShare = () => null,
  isDisableButtonTrack = false,
  isButtonTrack = false,
  isDisableButtonPendingEDI = false,
  isButtonPendingEDI = false,
  isDisableButtonSave = false,
  isLoadingButtonSave = false,
  isButtonSave = false,
  onClickSave = () => null,
  isDisableButtonDelete = false,
  isButtonDelete = false,
  onClickDelete = () => null,
  isButtonTitles,
  isDisableButtonTitles,
  onClickTitles,
  fullWidth = false,
  showDeleteModal = true,
  ovPortalNumber,
  ovSapNumber,
  showButtonMore = true,
  onClickSap = () => null,
  sapButtonsOptions,
  disableToolbar = false,
  isND = false,
  originOrder,
}: ToolBarProps) => {
  const isDesktop = useMediaQuery(`(min-width:${theme.breakpoints.md + 1}px)`)
  const [modalDelete, setModalDelete] = useState<boolean>(false)
  const [modalCancel, setModalCancel] = useState<boolean>(false)
  const [modalDuplicate, setModalDuplicate] = useState<boolean>(false)
  const [modalPostOn, setModalPostOn] = useState<boolean>(false)
  const [modalRescueData, setModalRescueData] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [sapModal, setSapModal] = useState<SapModalTypes | undefined>()
  const [modalTracking, setModalTracking] = useState<boolean>(false)
  const [modalPendingEDI, setModalPendingEDI] = useState<boolean>(false)
  const dispatch = useDispatch()
  const { isInternationalEdi, isYRBA } = useSelector(({ salesOrder }) => salesOrder)
  const {
    ovSelected: { statusOrdemVenda },
    isHAVAConectaOrder,
  } = useSelector(({ ov }) => ov)
  const { nr_cpnj_cpf, cd_clie, nm_clie, nr_pedi_rasc } = useSelector(
    ({ salesOrder }) => salesOrder.draftBody,
  )
  const { globalLoading } = useSelector((store) => store.structure)
  const { verifyPermission } = usePermissions()

  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.OV, action) || (isHAVAConectaOrder && !!orderNumber)
  }

  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClickRescueData = () => {
    if (onClickRescueData) {
      onClickRescueData()
    }
    setModalRescueData(false)
  }

  const handleClickSave = () => {
    if (onClickSave) {
      onClickSave()
    }
  }
  const handleClickDownload = () => {
    if (onClickDownload) {
      onClickDownload()
    }
  }

  const handleClickDuplicate = () => {
    setModalDuplicate(!modalDuplicate)
  }

  const handleClickPostOn = (array: ClientsListInterface[]) => {
    if (onClickPostOn) {
      onClickPostOn(array)
    }
  }
  const openCancel = () => {
    setModalCancel(true)
    dispatch(getReasonsRefusal())
  }

  const openPostOn = () => {
    setModalPostOn(true)
  }
  const openRescueData = () => {
    setModalRescueData(true)
  }

  const closePostOn = () => {
    setModalPostOn(false)
  }

  const handleClickShare = () => {
    if (onClickShare) {
      onClickShare()
    }
  }
  const handleClickTitles = () => {
    if (onClickTitles) {
      onClickTitles()
    }
  }

  const handleClickTrack = () => {
    setModalTracking(true)
  }

  const handleModalPendingEDI = () => {
    setModalPendingEDI(true)
  }

  const handleClickCancel = (data: CancelOvReason) => {
    if (onClickCancel) {
      onClickCancel(data)
      setModalCancel(false)
    }
  }

  const handleClickDelete = () => {
    if (onClickDelete) {
      onClickDelete()
      setModalDelete(false)
    }
  }

  return (
    <S.Container fullWidth={fullWidth}>
      <S.MainTitle
        fullWidth={fullWidth}
        status={orderStatus}
        type={
          (statusOrdemVenda
            ? StatusOV[statusOrdemVenda].split(' - ')[0]
            : StatusType.PENDENTE) as statusBgKey
        }
      >
        {orderStatus !== 'ordered' ? (
          <>
            <h2>Pedido</h2>
            <h3>{orderNumber ? `#${orderNumber}` : 'Aguardando dados do cliente'}</h3>
          </>
        ) : (
          <>
            <h2>
              {!!statusOrdemVenda &&
                `${statusOrdemVenda.split('-')[0]}- ${toFirsLetterUpper(
                  statusOrdemVenda.split('-')[1]?.trim(),
                )}`}
            </h2>
          </>
        )}
      </S.MainTitle>

      {orderStatus !== 'ordered' &&
        !!nr_cpnj_cpf &&
        !!cd_clie &&
        !!nm_clie &&
        orderNumber === nr_pedi_rasc && (
          <S.OrderType>
            <h2>
              Razão social <span> {nm_clie}</span>
            </h2>

            <S.OrderRow>
              <h2>
                SAP <span> {cd_clie}</span>
              </h2>
              <h2>
                CNPJ <span> {nr_cpnj_cpf}</span>
              </h2>
            </S.OrderRow>
          </S.OrderType>
        )}

      {orderStatus === 'ordered' && (
        <S.OvWrapper>
          <S.OvInfo>
            <h2>OV Portal</h2>
            <h3>{ovPortalNumber ?? '---'}</h3>
            <h3>{getEDIRowIcon(originOrder, undefined, true, isInternationalEdi)}</h3>
          </S.OvInfo>

          <S.OvInfo>
            <h2>OV SAP</h2>
            {ovSapNumber ? <h3>{ovSapNumber}</h3> : <h4>Não gerado</h4>}
          </S.OvInfo>
        </S.OvWrapper>
      )}

      <S.Actions>
        {isDesktop ? (
          <>
            {isButtonPendingEDI && (
              <S.ActionButton
                disabled={isDisableButtonPendingEDI || disableToolbar}
                fullfill={false}
                id="btn-share"
                onClick={handleModalPendingEDI}
              >
                <Icon icon="info" size="18" />
                Pendências
              </S.ActionButton>
            )}
            {isButtonShare && (
              <S.ActionButton
                disabled={isDisableButtonShare || disableToolbar}
                fullfill={false}
                id="btn-share"
                onClick={handleClickShare}
              >
                <Icon icon="share" size="18" />
                Compartilhar
              </S.ActionButton>
            )}
            {isButtonTitles && (
              <S.ActionButton
                disabled={isDisableButtonTitles || disableToolbar}
                fullfill={false}
                id="btn-title"
                onClick={handleClickTitles}
              >
                <Icon icon="request-quote" size="18" />
                Títulos
              </S.ActionButton>
            )}
            {isButtonDownload && orderStatus !== 'initial' && (
              <S.ActionButton
                disabled={isDisableButtonDownload || disableToolbar}
                fullfill={false}
                id="btn-download"
                onClick={handleClickDownload}
              >
                <Icon icon="download" size="18" />
                Baixar
              </S.ActionButton>
            )}
            {isButtonTrack && (
              <S.ActionButton
                disabled={isDisableButtonTrack || disableToolbar}
                fullfill={false}
                id="btn-tracking"
                onClick={handleClickTrack}
              >
                <Icon icon="track" size="18" />
                Tracking
              </S.ActionButton>
            )}
            {isButtonRescueData && (
              <S.ActionButton
                disabled={isDisableButtonRescueData || disableToolbar}
                fullfill={false}
                globalLoading={globalLoading}
                id="btn-rescue-data"
                onClick={() => !globalLoading && openRescueData()}
              >
                <Icon icon="history" size="18" />
                Resgatar Dados
              </S.ActionButton>
            )}
            {isButtonDuplicate && !isYRBA && (
              <S.ActionButton
                disabled={isDisableButtonDuplicate || disableToolbar}
                fullfill={false}
                globalLoading={globalLoading}
                id="btn-duplicate"
                onClick={() => !globalLoading && handleClickDuplicate()}
              >
                <Icon icon="copy" size="18" />
                Duplicar
              </S.ActionButton>
            )}

            {isButtonPostOn && !isYRBA && (
              <S.ActionButton
                disabled={isDisableButtonPostOn || disableToolbar}
                fullfill={false}
                globalLoading={globalLoading}
                id="btn-post-on"
                onClick={() => !globalLoading && openPostOn()}
              >
                <Icon icon="post-on" size="18" />
                Listar
              </S.ActionButton>
            )}
            {isButtonCancel && (
              <S.ActionButton
                disabled={isDisableButtonCancel || disableToolbar}
                fullfill={false}
                globalLoading={globalLoading}
                id="btn-cancel"
                onClick={() => !globalLoading && openCancel()}
              >
                <Icon icon="cancel" size="18" />
                Cancelar
              </S.ActionButton>
            )}
            {isButtonDelete && (
              <S.ActionButton
                disabled={isDisableButtonDelete || disableToolbar}
                fullfill={false}
                globalLoading={globalLoading}
                id="btn-delete"
                onClick={
                  globalLoading
                    ? undefined
                    : showDeleteModal
                      ? () => setModalDelete(true)
                      : () => handleClickDelete()
                }
              >
                <Icon icon="trash" size="18" />
                Excluir
              </S.ActionButton>
            )}

            {isButtonSave && ['initial', 'created', 'ordered'].includes(orderStatus) && (
              <S.ActionButton
                disabled={isDisableButtonSave || disableToolbar}
                fullfill={orderStatus !== 'ordered'}
                globalLoading={globalLoading}
                id="btn-save"
                onClick={() => !globalLoading && handleClickSave()}
              >
                {isLoadingButtonSave ? (
                  <S.CircularProgressStyled fullfill={orderStatus !== 'ordered'} size="2rem" />
                ) : (
                  <>
                    <Icon icon="save" size="18" /> Salvar
                  </>
                )}
              </S.ActionButton>
            )}

            {(sapButtonsOptions?.isValidToReturnFromSap ||
              sapButtonsOptions?.isValidToReturnToSalesArea) && (
              <S.ActionButton
                disabled={shouldDisable(ACTIONS_SCREENS.RETURN)}
                fullfill
                id="btn-return"
                onClick={() => setSapModal('returnModal')}
                sapButton={sapButtonsOptions?.isValidToReturnFromSap}
              >
                <Icon icon="undo" size="18" />
                Retornar
              </S.ActionButton>
            )}
            {sapButtonsOptions?.isValidToSendToSap && (
              <S.ActionButton
                disabled={shouldDisable(ACTIONS_SCREENS.SEND)}
                fullfill={!isHAVAConectaOrder && !orderNumber}
                id="btn-send"
                onClick={() => setSapModal(isND ? 'sendModalND' : 'sendModal')}
                sapButton={sapButtonsOptions?.isValidToSendToSap}
              >
                <Icon icon="send" size="18" />
                Enviar
              </S.ActionButton>
            )}
          </>
        ) : (
          <div>
            {showButtonMore && (
              <IconButton
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                aria-label="more"
                id="long-button"
                onClick={handleClick}
              >
                <S.HamburguerMenuWrapper>
                  <Icon color={theme.colors.primary.main} icon="menu" size="18" />
                </S.HamburguerMenuWrapper>
              </IconButton>
            )}
            <Menu
              anchorEl={anchorEl}
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'long-button',
              }}
              onClose={handleClose}
              open={open}
            >
              {isButtonShare && (
                <S.StyledMenuItem
                  disabled={isDisableButtonShare || disableToolbar}
                  id="btn-share-tablet"
                  onClick={() => {
                    handleClickShare()
                    handleClose()
                  }}
                >
                  Compartilhar
                </S.StyledMenuItem>
              )}

              {isButtonTitles && (
                <S.StyledMenuItem
                  disabled={isDisableButtonTitles || disableToolbar}
                  id="btn-title-tablet"
                  onClick={() => {
                    handleClickTitles()
                    handleClose()
                  }}
                >
                  Títulos
                </S.StyledMenuItem>
              )}
              {isButtonDownload && orderStatus !== 'initial' && (
                <S.StyledMenuItem
                  disabled={isDisableButtonDownload || disableToolbar}
                  id="btn-download-tablet"
                  onClick={() => {
                    handleClickDownload()
                    handleClose()
                  }}
                >
                  Baixar
                </S.StyledMenuItem>
              )}
              {isButtonTrack && (
                <S.StyledMenuItem
                  disabled={isDisableButtonTrack || disableToolbar}
                  id="btn-tracking-tablet"
                  onClick={() => {
                    handleClickTrack()
                    handleClose()
                  }}
                >
                  Tracking
                </S.StyledMenuItem>
              )}
              {isButtonRescueData && (
                <S.StyledMenuItem
                  disabled={isDisableButtonRescueData || disableToolbar}
                  id="btn-rescue-data-tablet"
                  onClick={() => {
                    openRescueData()
                    handleClose()
                  }}
                >
                  Resgatar Dados
                </S.StyledMenuItem>
              )}
              {isButtonDuplicate && !isYRBA && (
                <S.StyledMenuItem
                  disabled={isDisableButtonDuplicate || disableToolbar}
                  id="btn-duplicate-tablet"
                  onClick={() => {
                    handleClickDuplicate()
                    handleClose()
                  }}
                >
                  Duplicar
                </S.StyledMenuItem>
              )}
              {isButtonPostOn && !isYRBA && (
                <S.StyledMenuItem
                  disabled={isDisableButtonPostOn || disableToolbar}
                  id="btn-post-on-tablet"
                  onClick={() => {
                    openPostOn()
                    handleClose()
                  }}
                >
                  Listar
                </S.StyledMenuItem>
              )}
              {isButtonCancel && (
                <S.StyledMenuItem
                  disabled={isDisableButtonCancel || disableToolbar}
                  id="btn-cancel-tablet"
                  onClick={() => {
                    openCancel()
                    handleClose()
                  }}
                >
                  Cancelar
                </S.StyledMenuItem>
              )}

              {isButtonDelete && (
                <S.StyledMenuItem
                  disabled={isDisableButtonDelete || disableToolbar}
                  id="btn-delete-tablet"
                  onClick={() => {
                    if (showDeleteModal) {
                      setModalDelete(true)
                      handleClose()
                    } else {
                      handleClickDelete()
                    }
                  }}
                >
                  Excluir
                </S.StyledMenuItem>
              )}

              {isButtonSave && ['initial', 'created', 'ordered'].includes(orderStatus) && (
                <S.StyledMenuItem
                  disabled={isDisableButtonSave || disableToolbar}
                  id="btn-save-tablet"
                  onClick={() => {
                    handleClickSave()
                    handleClose()
                  }}
                >
                  Salvar
                </S.StyledMenuItem>
              )}

              {(sapButtonsOptions?.isValidToReturnFromSap ||
                sapButtonsOptions?.isValidToReturnToSalesArea) && (
                <S.StyledMenuItem
                  disabled={shouldDisable(ACTIONS_SCREENS.RETURN)}
                  id="btn-return-tablet"
                  onClick={() => {
                    setSapModal('returnModal')
                    handleClose()
                  }}
                >
                  Retornar
                </S.StyledMenuItem>
              )}

              {sapButtonsOptions?.isValidToSendToSap && (
                <S.StyledMenuItem
                  disabled={shouldDisable(ACTIONS_SCREENS.SEND)}
                  id="btn-send-tablet"
                  onClick={() => {
                    setSapModal('sendModal')
                    handleClose()
                  }}
                >
                  Enviar
                </S.StyledMenuItem>
              )}
            </Menu>
          </div>
        )}
      </S.Actions>

      <Modal
        buttonAction={handleClickDelete}
        buttonText="Excluir"
        description={
          ovPortalNumber
            ? 'Esta ação excluirá os itens inclusos nesta OV e eles serão removidos do seu pedido.'
            : 'Todos os materiais serão excluídos. Deseja continuar?'
        }
        open={modalDelete}
        returnText="Voltar"
        setOpen={setModalDelete}
        showReturn
        title={ovPortalNumber ? 'Excluir' : 'Excluir pedido'}
      />
      {modalCancel && (
        <ModalContentLayout
          closeAction={() => setModalCancel(false)}
          description={
            'Deste modo os itens inclusos nesta Ordem de venda deixarão de ser faturados. Para continuar insira a justificativa para a ação.'
          }
          title="Cancelar"
        >
          <CancelOvForm cancelAction={handleClickCancel} setOpen={setModalCancel} />
        </ModalContentLayout>
      )}

      <Modal
        buttonAction={handleClickRescueData}
        buttonText="Confirmar"
        description='Ao clicar em "Resgatar Dados", os campos listados a seguir serão resgatados do último pedido digitado e preenchidos automaticamente, sobrepondo qualquer outra informação: Tipo de OV, Condições de Pagamento, Prazo Extra, Desconto e Texto para expedição em caso de MTO.'
        open={modalRescueData}
        returnIcon={<></>}
        returnText="Voltar"
        setOpen={setModalRescueData}
        showReturn
        title="Resgatar dados"
      />
      <ModalDuplicate
        handleDuplicated={() => {
          onClickDuplicate()
          setModalDuplicate(false)
        }}
        open={modalDuplicate}
        setOpen={() => setModalDuplicate(!modalDuplicate)}
      />
      {orderNumber && (
        <ModalPostOn
          handleCancel={closePostOn}
          handlePostOn={handleClickPostOn}
          open={modalPostOn}
          orderNumber={orderNumber}
          setOpen={setModalPostOn}
        />
      )}
      {isButtonTrack && (
        <ModalTracking open={modalTracking} setOpen={() => setModalTracking(!modalTracking)} />
      )}
      <ModalPendingEDI
        open={modalPendingEDI}
        setOpen={() => setModalPendingEDI(!modalPendingEDI)}
      />
      <SapModal
        handleButtonAction={() => onClickSap()}
        sapModal={sapModal}
        setSapModal={setSapModal}
      />
    </S.Container>
  )
}
